/**
 * Expense-category settings — ``/settings/expense-categories`` (§10.5).
 *
 * Uses the shared ``<ManagedListTable>`` for DnD reorder, archive/restore
 * and inline edit. System-seeded categories ("Pfand", "Stock loss", …)
 * back the stocktake auto-Expense cascade + cash-drawer math via their
 * immutable ``code``, so they CAN be renamed and re-ordered freely — only
 * deactivate / hard-delete are blocked (Archive button disabled with a
 * tooltip). Once a system row is renamed away from its canonical name we
 * surface the fixed ``code`` in brackets (e.g. "Kasse (cash_drawer)") so
 * the original intent stays visible.
 */

import { useEffect, useState } from "react";
import {
  Alert,
  Form,
  Input,
  Modal,
  notification,
} from "antd";

import { ApiError, type ExpenseCategoryRow, api } from "../../api/client";
import { ManagedListTable } from "../../components/settings/ManagedListTable";
import { systemRowLabel } from "../../components/settings/systemRow";

interface FormValues {
  name: string;
}

export const ExpenseCategoriesPage = () => {
  const [rows, setRows] = useState<ExpenseCategoryRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ExpenseCategoryRow | null>(null);
  const [form] = Form.useForm<FormValues>();

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      // include_archived=true so the table can render the
      // "Archived (N)" collapse section.
      setRows(await api.listExpenseCategories({ include_archived: true }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ name: "" });
    setModalOpen(true);
  };

  const openEdit = (row: ExpenseCategoryRow) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({ name: row.name });
    setModalOpen(true);
  };

  const onSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        // System and non-system rows alike may be renamed (the server
        // binds logic to the immutable ``code``, not ``name``). Skip the
        // PATCH when nothing changed for a clean no-op UX.
        if (values.name.trim() !== editing.name) {
          await api.patchExpenseCategory(
            editing.id,
            { name: values.name.trim() },
            editing.updated_at,
          );
        }
      } else {
        await api.createExpenseCategory({
          name: values.name.trim(),
          sort_order: rows.length * 10,
        });
      }
      setModalOpen(false);
      setEditing(null);
      await refresh();
    } catch (err) {
      notification.error({
        message: editing ? "Update failed" : "Create failed",
        description: err instanceof ApiError ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const onArchive = async (row: ExpenseCategoryRow) => {
    setBusy(true);
    try {
      await api.deleteExpenseCategory(row.id);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onRestore = async (row: ExpenseCategoryRow) => {
    setBusy(true);
    try {
      await api.patchExpenseCategory(
        row.id,
        { active: true },
        row.updated_at,
      );
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const onReorder = async (orderedIds: number[]) => {
    try {
      await api.reorderExpenseCategories(orderedIds);
      await refresh();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
      await refresh();
    }
  };

  return (
    <>
      <ManagedListTable<ExpenseCategoryRow>
        title="Expense categories"
        description={
          "Used by the expense ledger. System categories back the stocktake auto-Expense cascade — they can be renamed (the fixed identity code shows in brackets) but not archived."
        }
        rows={rows}
        loading={loading}
        busy={busy}
        error={error}
        onErrorDismiss={() => setError(null)}
        addLabel="+ New category"
        // Surface the immutable code in brackets once a system category has
        // been renamed away from its canonical name (e.g. "Kasse
        // (cash_drawer)") so the original intent stays visible.
        labelOf={systemRowLabel}
        keyOf={(r) => r.id}
        isArchived={(r) => !r.active}
        isSystem={(r) => r.system_seeded}
        systemArchiveTooltip="System categories back the stocktake auto-Expense cascade and can't be archived."
        onReorder={onReorder}
        onCreate={openCreate}
        onEdit={openEdit}
        onArchive={onArchive}
        onRestore={onRestore}
      />
      <Modal
        title={editing ? "Edit category" : "New category"}
        open={modalOpen}
        onOk={() => void onSubmit()}
        onCancel={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        okText={editing ? "Save" : "Create"}
        confirmLoading={busy}
        destroyOnHidden
      >
        {editing?.system_seeded && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`System category — renaming only changes the display label; its identity code "${editing.code}" stays fixed.`}
          />
        )}
        <Form<FormValues> form={form} layout="vertical">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input maxLength={64} autoFocus />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ExpenseCategoriesPage;
